import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import Checkbox from 'components/shared/form/Checkbox';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import type Upload from 'hooks/useDirectUpload/Upload';
import { createBulkImport } from 'services/api/admin/bulkImports';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import DownloadFilesIcon from 'svg/DownloadFilesIcon';

type ImportTypeOption = { id: string; name: string };
const IMPORT_TYPES: ImportTypeOption[] = [{ id: 'users', name: 'Users' }];

// Windows browsers commonly report .csv files as application/vnd.ms-excel.
const CSV_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.csv'],
};

const CSV_COLUMNS = [
  { name: 'client', required: true, hint: 'Client name — each row can target a different client' },
  { name: 'email', required: true, hint: 'Also used to match existing users' },
  { name: 'name', required: true, hint: 'Full name' },
  { name: 'role', required: true, hint: 'Role name, e.g. Navigator' },
  { name: 'credential', required: false, hint: 'Abbreviation, e.g. RN' },
  {
    name: 'location_names',
    required: false,
    hint: 'Locations in the row’s client — the user gets each location’s groups; separate multiple with | or commas',
  },
  { name: 'external_id', required: false, hint: 'Stored on the user' },
];

const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(
  `${CSV_COLUMNS.map((c) => c.name).join(',')}\n`
)}`;

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

export default function BulkImportNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToastActions();

  const importType = IMPORT_TYPES.find((t) => t.id === searchParams.get('type')) ?? IMPORT_TYPES[0];
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  const completedUpload = uploads.find((u) => u.isComplete && u.signedId);
  const canSubmit = !!completedUpload && !uploading;

  const { mutate, isPending } = useMutation({
    mutationFn: createBulkImport,
    onSuccess: (res) => navigate(`/admin/bulk-import/${res.id}`),
    onError: () => addToast({ text: 'There was an error starting the import. Please try again.' }),
  });

  const submit = () => {
    if (!canSubmit || !completedUpload?.signedId) return;

    mutate({ importType: importType.id, signedId: completedUpload.signedId, sendInvite });
  };

  return (
    <AdminFormPage>
      <FormHeader title='New Bulk Import' />
      <Form>
        <FormSection>
          <SectionHeader>Import Details</SectionHeader>
          <InputGroup title='Resource Type'>
            <Select<ImportTypeOption>
              options={IMPORT_TYPES}
              value={importType}
              onChange={() => {}}
              getOptionValue={getId}
              getOptionLabel={getName}
              isDisabled
              placeholder='Users'
            />
          </InputGroup>
          <InputGroup title='CSV File'>
            <FileInputComponent
              onChange={setUploads}
              onUploading={setUploading}
              renderUploadsInternally
              allowedTypes={CSV_TYPES}
            />
            <FormatCard>
              <FormatHeader>
                <FormatTitle>CSV columns</FormatTitle>
                <TemplateLink href={TEMPLATE_HREF} download='bulk_user_import_template.csv'>
                  <DownloadFilesIcon width={12} height={12} />
                  Download template
                </TemplateLink>
              </FormatHeader>
              {CSV_COLUMNS.map((column) => (
                <FormatRow key={column.name}>
                  <ColumnName>{column.name}</ColumnName>
                  <RequirementPill $required={column.required}>
                    {column.required ? 'Required' : 'Optional'}
                  </RequirementPill>
                  <ColumnHint>{column.hint}</ColumnHint>
                </FormatRow>
              ))}
            </FormatCard>
          </InputGroup>
          <InputGroup>
            <Checkbox checked={sendInvite} onChange={setSendInvite} label='Send invites now' dataCyLabel='sendInvite' />
          </InputGroup>
        </FormSection>

        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={() => navigate('/admin/bulk-import')} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit || isPending} loading={isPending}>
              Upload &amp; Preview
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </AdminFormPage>
  );
}

const FormatCard = styled.div`
  margin-top: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const FormatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: ${colors.black02};
  border-bottom: 1px solid ${colors.black10};
`;

const FormatTitle = styled.span`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.black50};
`;

const TemplateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.primaryBlue};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FormatRow = styled.div`
  display: grid;
  grid-template-columns: 110px 72px 1fr;
  align-items: baseline;
  gap: 12px;
  padding: 8px 14px;

  & + & {
    border-top: 1px solid ${colors.black05};
  }
`;

const ColumnName = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: ${colors.black};
`;

const RequirementPill = styled.span<{ $required: boolean }>`
  justify-self: start;
  padding: 1px 8px;
  border-radius: 24px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  background: ${({ $required }) => ($required ? colors.primaryBlue10 : colors.black05)};
  color: ${({ $required }) => ($required ? colors.primaryBlue : colors.black50)};
`;

const ColumnHint = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black50};
`;
